/** @jsxImportSource @emotion/react */
'use client'

import { css, type SerializedStyles } from '@emotion/react'
import Link from 'next/link'
import type { ReactNode } from 'react'
import {
	animationTokens,
	borderRadiusTokens,
	colorTokens,
} from '../../styles/tokens'

type ButtonVariant = 'primary' | 'secondary'
type ButtonSize = 'default' | 'large'

const base = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: .75em 1em;
  font-family: system-ui;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1;
	letter-spacing: normal;
  text-decoration: none;
  border: 1px solid transparent;
  border-radius: ${borderRadiusTokens.control}px;
  cursor: pointer;
  transition: ${animationTokens.hoverTransition};
`

const variants: Record<ButtonVariant, SerializedStyles> = {
	primary: css`
    color: ${colorTokens.white};
    background-color: ${colorTokens.green100};

    &:hover {
      background-color: ${colorTokens.green200};
    }
  `,
	secondary: css`
    color: ${colorTokens.green100};
    background-color: transparent;
    border-color: ${colorTokens.green500};

    &:hover {
      color: ${colorTokens.green200};
      border-color: ${colorTokens.green500};
      background-color: rgb(67, 121, 67, .1);
    }
  `,
}

const sizes: Record<ButtonSize, SerializedStyles> = {
	default: css`
		font-size: 14px;
		@media (min-width: 768px) {
			font-size: 16px;
		}
	`,
	large: css`
		font-size: 16px;
		@media (min-width: 768px) {
			font-size: 18px;
		}
	`,
}

interface BaseProps {
	children: ReactNode
	variant?: ButtonVariant
	size?: ButtonSize
	/**
	 * Class applied to the underlying element. When a parent passes a `css` prop,
	 * Emotion compiles it to this className and composes it after the variant/size
	 * styles, so it can override them.
	 */
	className?: string
}

interface LinkProps extends BaseProps {
	/** Renders an anchor/Next link. Internal paths use next/link; http(s) URLs open in a new tab. */
	href: string
	onClick?: never
	type?: never
}

interface ActionProps extends BaseProps {
	href?: never
	onClick?: () => void
	type?: 'button' | 'submit'
}

type ButtonProps = LinkProps | ActionProps

const isHttpExternal = (href: string) =>
	/^https?:\/\//.test(href) || href.startsWith('//')

const isInternalPath = (href: string) =>
	href.startsWith('/') && !href.startsWith('//')

export default function Button({
	children,
	variant = 'primary',
	size = 'default',
	className,
	...rest
}: ButtonProps) {
	const styles = [base, variants[variant], sizes[size]]

	if ('href' in rest && rest.href !== undefined) {
		const { href } = rest

		if (isInternalPath(href)) {
			return (
				<Link css={styles} className={className} href={href}>
					{children}
				</Link>
			)
		}

		return (
			<a
				css={styles}
				className={className}
				href={href}
				{...(isHttpExternal(href)
					? { target: '_blank', rel: 'noopener noreferrer' }
					: {})}
			>
				{children}
			</a>
		)
	}

	return (
		<button
			css={styles}
			className={className}
			type={rest.type ?? 'button'}
			onClick={rest.onClick}
		>
			{children}
		</button>
	)
}
